import { Store } from "../lib/store";
import type { Block, TrackBlock } from "./blocks";

/**
 * One way of making sound: the voice player, MusicKit, the SoundCloud stream. The
 * engine owns exactly one active output at a time.
 */
export interface Output {
  /** Starts the block; resolves once it is audibly playing, rejects when it cannot play. */
  start(block: Block): Promise<void>;
  pause(): void;
  resume(): Promise<void>;
  stop(): void;
  /** Ramps the volume; used to duck a track out before whatever replaces it. */
  fade(toVolume: number, ms: number): Promise<void>;
  readonly position: number;
  readonly duration: number;
  /** Set by the engine: the block finished on its own. */
  onEnded: (() => void) | null;
}

export interface Outputs {
  voice: Output;
  appleMusic: Output;
  soundCloud: Output;
}

export type EngineStatus =
  | "idle"
  | "loading"
  | "playing"
  | "paused"
  /** At the end of a queue that may still grow: a live station's next batch is coming. */
  | "waiting"
  /** The browser refused to start audio without a click. */
  | "blocked"
  | "ended";

export interface EngineState {
  queueID: string | null;
  title: string;
  blocks: Block[];
  index: number;
  status: EngineStatus;
  position: number;
  duration: number;
  notice: string | null;
}

export interface QueueSource {
  id: string;
  title: string;
  blocks: Block[];
  /** More blocks will be appended (a live station), so the end is a wait, not an end. */
  isLive: boolean;
}

const START_TIMEOUT_MS = 15_000;
const DUCK_MS = 300;
/** Pressing previous this far into a block restarts it instead of going back. */
const RESTART_THRESHOLD_SECONDS = 3;

/**
 * Plays a queue of voice and track blocks strictly one after another — a voice and a
 * track never sound at once — the way the phone's PlaybackEngine does:
 * a block that cannot play is skipped with a notice, a track being cut short is
 * ducked out first, and a live queue waits at its end for the next batch.
 */
export class PlaybackEngine extends Store<EngineState> {
  /** The current block changed: the station reports the cursor from here. */
  onAdvance: ((block: Block) => void) | null = null;
  /** A track failed at play time: the station asks the server for a replacement. */
  onTrackFailed: ((block: TrackBlock) => void) | null = null;

  private isLive = false;
  private active: Output | null = null;
  /** Bumped on every transition; a late callback from an older one is ignored. */
  private generation = 0;
  private ticker: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly outputs: Outputs) {
    super({
      queueID: null,
      title: "",
      blocks: [],
      index: 0,
      status: "idle",
      position: 0,
      duration: 0,
      notice: null,
    });
  }

  get currentBlock(): Block | null {
    return this.state.blocks[this.state.index] ?? null;
  }

  /** Tracks still to come after the current block. */
  get remainingTrackCount(): number {
    return this.state.blocks.slice(this.state.index + 1).filter((block) => block.kind === "track")
      .length;
  }

  async load(source: QueueSource, startIndex = 0): Promise<void> {
    this.halt();
    this.isLive = source.isLive;
    this.update({
      queueID: source.id,
      title: source.title,
      blocks: source.blocks,
      index: Math.max(0, Math.min(startIndex, source.blocks.length)),
      notice: null,
    });
    await this.playCurrent();
  }

  /** New blocks at the end of the queue; a waiting station picks them up at once. */
  append(blocks: Block[]): void {
    const known = new Set(this.state.blocks.map((block) => block.id));
    const fresh = blocks.filter((block) => !known.has(block.id));
    if (fresh.length === 0) return;
    this.update({ blocks: [...this.state.blocks, ...fresh] });
    if (this.state.status === "waiting") void this.playCurrent();
  }

  /** Drops everything from `position` on, never the block that is playing. */
  truncate(fromPosition: number): void {
    const kept = this.state.blocks.filter(
      (block, index) =>
        index <= this.state.index || block.position === null || block.position < fromPosition,
    );
    if (kept.length !== this.state.blocks.length) this.update({ blocks: kept });
  }

  /** The queue is no longer growing: its end is the end. */
  finishLive(): void {
    this.isLive = false;
    if (this.state.status === "waiting") this.update({ status: "ended" });
  }

  async togglePause(): Promise<void> {
    const { status } = this.state;
    if (status === "playing") {
      this.active?.pause();
      this.update({ status: "paused" });
    } else if (status === "paused" && this.active) {
      await this.active.resume();
      this.update({ status: "playing" });
    } else if (status === "blocked" || status === "ended" || status === "idle") {
      await this.playCurrent();
    }
  }

  async next(): Promise<void> {
    if (this.state.index >= this.state.blocks.length) return;
    await this.duckOut();
    await this.moveTo(this.state.index + 1);
  }

  async previous(): Promise<void> {
    if (this.state.position > RESTART_THRESHOLD_SECONDS || this.state.index === 0) {
      await this.duckOut();
      await this.playCurrent();
      return;
    }
    await this.duckOut();
    await this.moveTo(this.state.index - 1);
  }

  async jump(index: number): Promise<void> {
    if (index < 0 || index >= this.state.blocks.length) return;
    await this.duckOut();
    await this.moveTo(index);
  }

  stop(): void {
    this.halt();
    this.isLive = false;
    this.update({
      queueID: null,
      title: "",
      blocks: [],
      index: 0,
      status: "idle",
      position: 0,
      duration: 0,
      notice: null,
    });
  }

  dismissNotice(): void {
    this.update({ notice: null });
  }

  // MARK: - Sequencing

  private async moveTo(index: number): Promise<void> {
    this.update({ index });
    await this.playCurrent();
  }

  private async playCurrent(): Promise<void> {
    this.halt();
    const generation = this.generation;
    const block = this.currentBlock;
    if (!block) {
      this.update({ status: this.isLive ? "waiting" : "ended", position: 0, duration: 0 });
      return;
    }
    const output = this.outputFor(block);
    this.active = output;
    output.onEnded = () => {
      if (generation === this.generation) void this.moveTo(this.state.index + 1);
    };
    this.update({ status: "loading", position: 0, duration: block.duration });
    this.onAdvance?.(block);
    try {
      await withTimeout(output.start(block), START_TIMEOUT_MS);
    } catch (error) {
      if (generation !== this.generation) return;
      output.stop();
      if (isAutoplayRefusal(error)) {
        this.update({ status: "blocked" });
        return;
      }
      if (block.kind === "track") this.onTrackFailed?.(block);
      this.update({ notice: `Skipped “${block.title}”: it would not play here.` });
      await this.moveTo(this.state.index + 1);
      return;
    }
    if (generation !== this.generation) return;
    this.update({ status: "playing", duration: output.duration || block.duration });
    this.ticker = setInterval(() => {
      if (generation !== this.generation || this.active !== output) return;
      this.update({ position: output.position, duration: output.duration || block.duration });
    }, 250);
  }

  /** A track cut short is faded out first rather than chopped. */
  private async duckOut(): Promise<void> {
    const block = this.currentBlock;
    if (this.active && block?.kind === "track" && this.state.status === "playing") {
      await this.active.fade(0, DUCK_MS).catch(() => undefined);
    }
  }

  private halt(): void {
    this.generation += 1;
    if (this.ticker) clearInterval(this.ticker);
    this.ticker = null;
    if (this.active) {
      this.active.onEnded = null;
      this.active.stop();
      this.active = null;
    }
  }

  private outputFor(block: Block): Output {
    if (block.kind === "voice") return this.outputs.voice;
    return block.provider === "soundcloud" ? this.outputs.soundCloud : this.outputs.appleMusic;
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("start timed out")), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

/** The browser wants a click before it makes sound. MusicKit reports it with its own
 * error type, so the name is what is checked, not the class. */
export function isAutoplayRefusal(error: unknown): boolean {
  return (error as { name?: unknown } | null)?.name === "NotAllowedError";
}
